import { TrashIcon } from '@phosphor-icons/react';
import { Button } from '@admin-panel/ui';
import { updateAttributesOfCdnImage } from '@admin-panel/utils';

export type VisualSearchProduct = {
  id: string;
  name: string;
  assetUrl: string;
  deepLink: string;
  rank: number | null;
  lkStyleNumber: string;
  inventoryDepth?: number;
  jitDepth?: number;
};

export type WidgetSource = {
  itemKey: string;
  componentGroup: string;
  widgetId: string;
  productLabel: string;
  isolatedImageUrl: string;
  products: Array<VisualSearchProduct>;
};

export type ComponentWidget = {
  id: string;
  title: string;
  description: string;
  products: Array<VisualSearchProduct>;
};

export type SourceItem = {
  itemKey: string;
  productType: string;
  componentGroup: string;
  productLabel: string;
  productDescription: string;
  productTypeFilters: Array<string>;
  colorFilters: Array<string>;
  searchSeed: string;
  isolationNotes: string;
  isolatedImageUrl: string;
  mappedLkStyles: Array<string>;
  mappedProductImages: Record<string, string>;
  status: string;
  errorMessage: string;
  rawPayload: Record<string, unknown>;
};

export type EnrichmentPayload = {
  status?: string;
  builtAt?: string | null;
  buildError?: string;
  lookCopy?: {
    lookName?: string;
    lookHook?: string;
    stylingBlurb?: string;
  };
  widgets: Array<ComponentWidget>;
  widgetSources: Array<WidgetSource>;
  sourceItems: Array<SourceItem>;
};

type GeneratedWidgetListProps = {
  payload?: unknown;
  showSources?: boolean;
  emptyText?: string;
  onRemoveProduct?: (widgetId: string, productIdentifier: string) => void | Promise<void>;
  removingProductKey?: string | null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const getString = (value: unknown) => (typeof value === 'string' ? value.trim() : '');

const getNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const buildImageUrl = (value: string, width: number) =>
  value ? updateAttributesOfCdnImage(value, { width, quality: 100 }) || value : '';

const getProductIdentifier = (product: VisualSearchProduct) =>
  getString(product.id) ||
  getString(product.deepLink) ||
  getString(product.lkStyleNumber) ||
  getString(product.name);

const normalizeStringArray = (value: unknown): Array<string> =>
  Array.isArray(value)
    ? value
        .map(item => getString(item))
        .filter(Boolean)
    : [];

const normalizeVisualSearchProduct = (value: unknown): VisualSearchProduct | null => {
  if (!isRecord(value)) {
    return null;
  }

  return {
    id: getString(value.id) || getString(value.catalog_product_id),
    name: getString(value.name),
    assetUrl: getString(value.asset_url ?? value.assetUrl),
    deepLink: getString(value.deep_link ?? value.deepLink),
    rank: getNumber(value.rank),
    lkStyleNumber: getString(value.lk_style_number ?? value.lkStyleNumber),
  };
};

const normalizeComponentWidget = (value: unknown): ComponentWidget | null => {
  if (!isRecord(value)) {
    return null;
  }

  const data = isRecord(value.data) ? value.data : {};

  return {
    id: getString(value.id),
    title: getString(data.title),
    description: getString(data.description),
    products: Array.isArray(data.products)
      ? (data.products
          .map(normalizeVisualSearchProduct)
          .filter(Boolean) as Array<VisualSearchProduct>)
      : [],
  };
};

const normalizeWidgetSource = (value: unknown): WidgetSource | null => {
  if (!isRecord(value)) {
    return null;
  }

  return {
    itemKey: getString(value.item_key),
    componentGroup: getString(value.component_group),
    widgetId: getString(value.widget_id),
    productLabel: getString(value.product_label),
    isolatedImageUrl: getString(value.isolated_image_url),
    products: Array.isArray(value.products)
      ? (value.products
          .map(normalizeVisualSearchProduct)
          .filter(Boolean) as Array<VisualSearchProduct>)
      : Array.isArray(value.matches)
        ? (value.matches
            .map(normalizeVisualSearchProduct)
            .filter(Boolean) as Array<VisualSearchProduct>)
      : [],
  };
};

const normalizeSourceItem = (value: unknown): SourceItem | null => {
  if (!isRecord(value)) {
    return null;
  }

  const componentGroup = getString(value.component_group) || getString(value.product_type);
  const productType = getString(value.product_type) || componentGroup;

  return {
    productType,
    itemKey: getString(value.item_key),
    componentGroup,
    productLabel: getString(value.product_label),
    productDescription: getString(value.product_description),
    productTypeFilters: normalizeStringArray(value.product_type_filters),
    colorFilters: normalizeStringArray(value.color_filters),
    searchSeed: getString(value.search_seed),
    isolationNotes: getString(value.isolation_notes),
    isolatedImageUrl: getString(value.isolated_image_url),
    mappedLkStyles: normalizeStringArray(value.mapped_lk_styles),
    mappedProductImages: isRecord(value.mapped_product_images)
      ? (Object.fromEntries(
          Object.entries(value.mapped_product_images).filter(
            ([key, mappedValue]) => key && typeof mappedValue === 'string' && mappedValue.trim(),
          ),
        ) as Record<string, string>)
      : {},
    status: getString(value.status),
    errorMessage: getString(value.error_message),
    rawPayload: isRecord(value.raw_payload) ? value.raw_payload : {},
  };
};

export const normalizeEnrichmentPayload = (value: unknown): EnrichmentPayload => {
  if (Array.isArray(value)) {
    return {
      widgets: [],
      widgetSources: [],
      sourceItems: value.map(normalizeSourceItem).filter(Boolean) as Array<SourceItem>,
    };
  }

  if (!isRecord(value)) {
    return {
      widgets: [],
      widgetSources: [],
      sourceItems: [],
    };
  }

  return {
    status: getString(value.status),
    builtAt:
      typeof value.built_at === 'string'
        ? value.built_at
        : typeof value.builtAt === 'string'
          ? value.builtAt
          : null,
    buildError: getString(value.build_error ?? value.buildError),
    lookCopy: isRecord(value.look_copy)
      ? {
          lookName: getString(value.look_copy.look_name),
          lookHook: getString(value.look_copy.look_hook),
          stylingBlurb: getString(value.look_copy.styling_blurb),
        }
      : undefined,
    widgets: Array.isArray(value.items)
      ? (value.items.map(normalizeComponentWidget).filter(Boolean) as Array<ComponentWidget>)
      : Array.isArray(value.widgets)
        ? (value.widgets.map(normalizeComponentWidget).filter(Boolean) as Array<ComponentWidget>)
      : [],
    widgetSources: Array.isArray(value.widget_sources)
      ? (value.widget_sources.map(normalizeWidgetSource).filter(Boolean) as Array<WidgetSource>)
      : Array.isArray(value.widgetSources)
        ? (value.widgetSources.map(normalizeWidgetSource).filter(Boolean) as Array<WidgetSource>)
      : [],
    sourceItems: Array.isArray(value.source_items)
      ? (value.source_items.map(normalizeSourceItem).filter(Boolean) as Array<SourceItem>)
      : Array.isArray(value.sourceItems)
        ? (value.sourceItems.map(normalizeSourceItem).filter(Boolean) as Array<SourceItem>)
      : [],
  };
};

const formatStatusLabel = (status?: string) => {
  if (!status) {
    return 'Not built';
  }

  return status.replace(/_/g, ' ');
};

const formatBuiltAt = (builtAt?: string | null) => {
  if (!builtAt) {
    return null;
  }

  const parsed = new Date(builtAt);
  if (Number.isNaN(parsed.getTime())) {
    return builtAt;
  }

  return parsed.toLocaleString();
};

const getStatusTextClassName = (status?: string) => {
  switch (status) {
    case 'ready':
      return 'text-emerald-700';
    case 'building':
    case 'pending':
      return 'text-sky-700';
    case 'pending_input':
      return 'text-amber-700';
    case 'failed':
      return 'text-rose-700';
    default:
      return 'text-muted-foreground';
  }
};

const ProductThumb = ({
  imageUrl,
  alt,
  fallback,
  className = 'h-10 w-10',
}: {
  imageUrl: string;
  alt: string;
  fallback: string;
  className?: string;
}) => (
  <div
    className={`overflow-hidden rounded-md border border-border/80 bg-muted/30 ${className}`}
    title={fallback}
  >
    {imageUrl ? (
      <img src={imageUrl} alt={alt} className="h-full w-full object-cover" />
    ) : (
      <div className="flex h-full w-full items-center justify-center px-1 text-[10px] font-medium text-muted-foreground">
        {fallback}
      </div>
    )}
  </div>
);

const widgetSourcesForWidget = (widget: ComponentWidget, sources: Array<WidgetSource>) =>
  sources.filter(source => {
    if (source.widgetId && source.widgetId === widget.id) {
      return true;
    }

    if (source.widgetId && widget.id.endsWith(`_${source.widgetId}`)) {
      return true;
    }

    return false;
  });

export const GeneratedWidgetList = ({
  payload,
  showSources = false,
  emptyText = 'No generated widgets yet.',
  onRemoveProduct,
  removingProductKey = null,
}: GeneratedWidgetListProps) => {
  const enrichment = normalizeEnrichmentPayload(payload);

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span className={getStatusTextClassName(enrichment.status)}>
          {formatStatusLabel(enrichment.status)}
        </span>
        {enrichment.builtAt ? <span>Updated {formatBuiltAt(enrichment.builtAt)}</span> : null}
      </div>

      {enrichment.buildError ? (
        <p className="m-0 text-xs text-rose-700">{enrichment.buildError}</p>
      ) : null}

      {enrichment.widgets.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">{emptyText}</p>
      ) : (
        enrichment.widgets.map(widget => {
          const sources = widgetSourcesForWidget(widget, enrichment.widgetSources);
          const isBaseProductWidget =
            widget.title.trim().toLowerCase() === 'base product' ||
            widget.id.endsWith('_base_product');
          const isWidgetRemovable = Boolean(onRemoveProduct && !isBaseProductWidget);

          return (
            <article
              key={widget.id || widget.title}
              className="space-y-2 rounded-md border border-border/70 p-2.5"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="m-0 text-sm font-medium text-foreground">{widget.title || 'Widget'}</p>
                  {widget.description ? (
                    <p className="m-0 mt-1 text-[11px] text-muted-foreground">
                      {widget.description}
                    </p>
                  ) : null}
                </div>
              </div>

              {showSources && sources.length > 0 ? (
                <div className="space-y-2">
                  {sources.map(source => (
                    <div
                      key={`${widget.id}-${source.itemKey}`}
                      className="space-y-2 rounded-md bg-muted/30 p-2"
                    >
                      <div className="grid gap-2 sm:grid-cols-[80px_1fr]">
                        <div className="flex justify-start">
                          <ProductThumb
                            imageUrl={buildImageUrl(source.isolatedImageUrl, 160)}
                            alt={source.productLabel || source.itemKey}
                            fallback={source.productLabel || source.itemKey || 'No image'}
                            className="h-20 w-16"
                          />
                        </div>

                        <div className="space-y-1">
                          <p className="m-0 text-xs font-medium text-foreground">
                            {source.productLabel || source.itemKey}
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {source.products.slice(0, 8).map(match => (
                              <ProductThumb
                                key={`${source.itemKey}-${match.id}-${match.rank ?? 0}`}
                                imageUrl={buildImageUrl(match.assetUrl, 120)}
                                alt={match.name || match.id}
                                fallback={match.lkStyleNumber || match.id || 'No image'}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : widget.products.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {(isWidgetRemovable ? widget.products : widget.products.slice(0, 8)).map(
                    (product, previewIndex) => {
                      const productIdentifier = getProductIdentifier(product);
                      const productKey = `${widget.id}-${product.id || productIdentifier}-${previewIndex}`;
                      const isRemoving =
                        !!productIdentifier &&
                        removingProductKey === `${widget.id}::${productIdentifier}`;

                      return isWidgetRemovable && productIdentifier && onRemoveProduct ? (
                        <div
                          key={productKey}
                          className="flex w-20 flex-col gap-1 rounded-md border border-border/70 bg-muted/20 p-1.5"
                        >
                          <ProductThumb
                            imageUrl={buildImageUrl(product.assetUrl, 120)}
                            alt={product.name || product.id}
                            fallback={product.lkStyleNumber || product.id || 'No image'}
                            className="h-16 w-full"
                          />
                          <Button
                            type="button"
                            size="small"
                            variant="ghost"
                            tone="destructive"
                            icon={<TrashIcon size={12} />}
                            isLoading={isRemoving}
                            isDisabled={isRemoving}
                            onClick={() => void onRemoveProduct(widget.id, productIdentifier)}
                            className="justify-center px-1"
                          >
                            Remove
                          </Button>
                        </div>
                      ) : (
                        <ProductThumb
                          key={productKey}
                          imageUrl={buildImageUrl(product.assetUrl, 120)}
                          alt={product.name || product.id}
                          fallback={product.lkStyleNumber || product.id || 'No image'}
                        />
                      );
                    },
                  )}
                </div>
              ) : (
                <p className="text-[11px] text-muted-foreground">No generated products yet.</p>
              )}
            </article>
          );
        })
      )}
    </section>
  );
};
